
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import Head from 'next/head';
import { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next';
import dynamic from 'next/dynamic';
import { TaskTimer } from 'tasktimer';
import VideoDetails from 'models/VideoDetails';
import { useAuth, withForcePlanToLoggedInUnsubs } from 'context/authContext';
import { useFetch } from 'api/useFetch';
import { Play as PlayApi, Video } from 'typings/api';
import styles from './index.module.scss';
import { getSharedApiClient } from 'api/client';
import { usePopup } from 'context/popupContext';
import Layout from 'components/Layout';
import { PopupType, Namespace } from 'enums';
import { basicSEO, createRedirectIfNeeded, Permalink } from 'helpers';
import Analytics from 'helpers/Analytics';
import AppLink from 'components/AppLink/AppLink';
import { useRouter } from 'next/router';
import { authorizeUserServerSide } from 'api/firebaseAdmin';
import { StaticPathParams } from 'typings/staticPaths';
import Play from 'models/Play';
const VideoComponent = dynamic(import('components/Video/Video'), { ssr: false });

let video: HTMLVideoElement;
let analyticsTimer: TaskTimer;
const TICK_UPDATE_WATCH_PROGRESS = 20;
const TICK_PLAY_UPDATE_ANALYTICS = 1;
const TICK_TRAILER_UPDATE_ANALYTICS_1 = 1;
const TICK_TRAILER_UPDATE_ANALYTICS_2 = 5;

interface Props {
  play: PlayApi;
  isPreview?: boolean;
  countryCode: string;
}

const Watch: NextPage<Props> = ({ play, isPreview = false, countryCode }) => {
  const { setPopupConfig } = usePopup();
  const { lang, t } = useTranslation(Namespace.Common);
  const { isUserLoaded } = useAuth();
  const [showTimeoutError, setShowTimeoutError] = useState(false);
  const [displayDonate, setDisplayDonate] = useState(false);
  const [videoDetails, setVideoDetails] = useState<VideoDetails>(null);
  const router = useRouter();
  const { data, error, mutate } = useFetch<Video>(
    `video/${play[isPreview ? 'preview' : 'video']?.id}`,
    lang,
    isUserLoaded,
  );

  function requestNewVideoDetails() {
    return mutate().then((video) => video.custom_data);
  }

  const loading = !error && !data;

  useEffect(() => {
    if (data) {
      const videoDetails = VideoDetails.fromJSON(data);
      setVideoDetails(videoDetails);
    }
  }, [data]);

  useEffect(() => {
    return () => {
      setVideoDetails(null);
      destroyAnalyticsTimer();
    };
  }, []);

  useEffect(() => {
    if (error) {
      setPopupConfig({
        popupType: PopupType.BuySubscription,
        onClose: onVideoClose,
      });
    }
  }, [error]);

  function destroyAnalyticsTimer() {
    analyticsTimer?.stop();
    analyticsTimer?.removeAllListeners();
    analyticsTimer = null;
  }

  /*
   * LET THERE BE ANALYTICS
   * Send update to the server every TICK_UPDATE (in seconds) should the video has not been paused.
   */
  function trackViewAnalytics() {
    if (!analyticsTimer) {
      analyticsTimer = new TaskTimer(1000);
      let onTick: () => void;
      // performance tracking
      if (!isPreview) {
        onTick = function () {
          const ticks = analyticsTimer.tickCount;

          if (ticks === TICK_PLAY_UPDATE_ANALYTICS) {
            Analytics.v2_playView(play.title, play.theatre.name, 'start');
          }

          // send watch progress to the backend every 20 seconds
          if (ticks > 0 && ticks % TICK_UPDATE_WATCH_PROGRESS === 0) {
            getSharedApiClient().updateVideoProgress(
              data.id,
              Math.floor(video.currentTime),
              TICK_UPDATE_WATCH_PROGRESS,
              videoDetails.sessionId,
            );
          }
        };
      } else {
        // trailer tracking
        onTick = function () {
          const ticks = analyticsTimer.tickCount;
          if (ticks === TICK_TRAILER_UPDATE_ANALYTICS_1) {
            Analytics.v2_trailerView(play.title, play.theatre.name, 'start');
          } else if (ticks === TICK_TRAILER_UPDATE_ANALYTICS_2) {
            Analytics.v2_trailerView(play.title, play.theatre.name, `${TICK_TRAILER_UPDATE_ANALYTICS_2}_seconds`);
            destroyAnalyticsTimer();
          }
        };
      }

      analyticsTimer.on('tick', onTick);
      analyticsTimer.start();
    } else {
      analyticsTimer?.resume();
    }
  }

  const onVideoPlay = () => {
    trackViewAnalytics();
  };

  // stop tracking when playback ends
  const onPlaybackEnded = () => {
    if (isPreview) {
      Analytics.v2_trailerView(play.title, play.theatre.name, 'finish');
    } else {
      Analytics.v2_playView(play.title, play.theatre.name, 'finish');
    }
    destroyAnalyticsTimer();
  };

  // timeout error (empty buffer for too long etc.)
  const onTimeoutError = () => {
    setShowTimeoutError(true);
  };
  const onError = () => {
    destroyAnalyticsTimer();
  };
  const onEndCreditsStarted = () => {
    if (play.theatre.donations_allowed) {
      setDisplayDonate(true);
    }
  };
  const onVideoClose = () => {
    destroyAnalyticsTimer();
    router.back();
  };
  const onVideoPause = () => {
    analyticsTimer?.pause();
  };
  const onInit = (video_: HTMLVideoElement) => {
    video = video_;
  };

  if (showTimeoutError) {
    return (
      <div className="player-error">
        <div className="shaka-text-text-titles">
          <h1>{play.title}</h1>
          <h2>{play.theatre.name}</h2>
        </div>
        <button className="shaka-close-button" onClick={onVideoClose}>
          {t('input_close')}
        </button>
        <div className="player-error-content">
          <h2>{t('play-detail:playback_error_title')}</h2>
          <p>{t('play-detail:playback_error_content')}</p>
          <button className="btn btn-primary" onClick={onVideoClose}>
            {t('play-detail:playback_error_action')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <Layout hiddenWrappers countryCode={countryCode}>
      <Head>
        {basicSEO(
          t('seo:watch_performance_title', { title: play.title, theatre: play.theatre?.name }),
          play.about,
          play.poster_url,
        )}
      </Head>
      <SkeletonTheme baseColor="#22222E" highlightColor="#2D2D3A">
        <div className={styles.container}>
          {loading && (
            <>
              <div className={['shaka-text-text-titles', styles.noBg].join(' ')}>
                <h1>
                  <Skeleton />
                </h1>
                <h2>
                  <Skeleton />
                </h2>
              </div>
              <div className={styles.controlsPlaceholder}>
                <Skeleton />
              </div>
              <div className={['shaka-close-button', styles.closeButtonPlaceholder].join(' ')}>
                <Skeleton />
              </div>
            </>
          )}
          {!loading && !error && videoDetails && (
            <VideoComponent
              videoDetails={videoDetails}
              onError={onError}
              onTimeoutError={onTimeoutError}
              onEndCreditsStarted={onEndCreditsStarted}
              onPlay={onVideoPlay}
              onPlaybackEnded={onPlaybackEnded}
              onClose={onVideoClose}
              onPause={onVideoPause}
              onInit={onInit}
              onNewDRMCustomDataRequest={requestNewVideoDetails}
            />
          )}
          {displayDonate && (
            <div className="donate-play">
              <img src={play.theatre.poster_url} alt={play.theatre.name} />
              <p>
                {t('play-detail:donate_bubble_title')}
                <br />
                <AppLink href={Permalink.donateTheatre(play.theatre)}>{t('play-detail:donate_bubble_link')}</AppLink>
              </p>
            </div>
          )}
        </div>
      </SkeletonTheme>
    </Layout>
  );
};

 async function _getServerSideProps(
  ctx: GetServerSidePropsContext<StaticPathParams>,
): Promise<GetServerSidePropsResult<Props>> {
  const playSlug = ctx.query?.slug as string;

  const { locale, apiClient, countryCode } = await authorizeUserServerSide(ctx);

  const redirect = createRedirectIfNeeded(ctx, locale);
  if (redirect) {
    return redirect;
  }

  try {
    // PLAY check
    const [id, ...slug] = (playSlug as string).split('-');
    let res: PlayApi;

    // old urls without ids
    if (isNaN(+id)) {
      res = await apiClient.getPlayDetail(slug.join('-'));
    } else {
      res = await apiClient.getPlayDetail(+id);
    }
    const play = Play.fromJSON(res);
    const localisedSlug = play.localisedSlug(locale);

    if (slug.join('-') !== localisedSlug) {
      return {
        redirect: {
          permanent: true,
          destination: Permalink.watch({ ...play, slug: localisedSlug }, locale),
        },
      };
    }

    return {
      props: {
        play: res,
        countryCode,
      },
    };
  } catch (e) {
    return {
      notFound: true,
    };
  }
}

export default withForcePlanToLoggedInUnsubs(Watch);


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/watch-online/[slug]/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  